import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useAPI } from "../contexts/APIContext";

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

export default function RedeemPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, loading } = useAuth();
  const { apiClient } = useAPI();

  const token = searchParams.get("token");
  const hasRedeemedRef = useRef(false);

  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMessage, setErrorMessage] = useState("");
  const [premiumUntil, setPremiumUntil] = useState("");

  useEffect(() => {
    if (loading) return;

    if (!token) {
      setStatus("error");
      setErrorMessage("Invalid or missing gift token.");
      return;
    }

    if (!isAuthenticated) {
      const redeemPath = `/redeem?token=${encodeURIComponent(token)}`;
      navigate(`/auth?redirect=${encodeURIComponent(redeemPath)}`, { replace: true });
      return;
    }

    if (!apiClient || hasRedeemedRef.current) return;
    hasRedeemedRef.current = true;

    const redeemGift = async () => {
      setStatus("loading");
      setErrorMessage("");

      try {
        const authToken = apiClient.getAuthToken();
        const response = await fetch(`${apiClient.baseURL}/api/gifts/redeem`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({ token }),
          credentials: "include",
        });

        let payload = {};
        try { payload = await response.json(); } catch { payload = {}; }

        if (!response.ok) {
          if (response.status === 400) setErrorMessage("This gift has already been redeemed.");
          else if (response.status === 403) setErrorMessage("This gift was sent to a different email.");
          else if (response.status === 404) setErrorMessage("Invalid gift token.");
          else setErrorMessage("Something went wrong. Please try again.");
          setStatus("error");
          return;
        }

        const resolvedPremiumUntil = payload?.data?.premium_until || payload?.premium_until || "";
        setPremiumUntil(resolvedPremiumUntil);
        setStatus("success");
      } catch {
        setStatus("error");
        setErrorMessage("Something went wrong. Please try again.");
      }
    };

    redeemGift();
  }, [loading, token, isAuthenticated, navigate, apiClient]);

  return (
    <div
      className="min-h-screen relative px-4 sm:px-6 lg:px-8 pt-24 pb-12"
      style={{
        background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)",
        fontFamily: "'Inter', 'Fredoka', sans-serif",
      }}
    >
      {/* Soft brand blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div style={{ position: "absolute", top: -200, left: -180, width: 520, height: 520, borderRadius: "50%", background: "rgba(0,101,248,0.08)", filter: "blur(30px)" }} />
        <div style={{ position: "absolute", bottom: -240, right: -220, width: 560, height: 560, borderRadius: "50%", background: "rgba(0,202,255,0.08)", filter: "blur(30px)" }} />
      </div>

      <div className="relative z-10 max-w-xl mx-auto">
        <div
          className="text-center overflow-hidden p-9"
          style={{
            background: "#FFFFFF",
            border: "2px solid rgba(11,26,51,0.06)",
            borderRadius: 28,
            boxShadow: "0px 5px 0px rgba(11,26,51,0.08), 0px 24px 64px rgba(11,26,51,0.10)",
          }}
        >
          {status === "loading" && (
            <div className="flex flex-col items-center gap-4 py-8">
              <div
                className="flex items-center justify-center"
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 18,
                  background: "linear-gradient(135deg, #0065F8 0%, #00CAFF 100%)",
                  boxShadow: "0px 4px 0px #073B8E",
                }}
              >
                <span style={{ width: 26, height: 26, borderRadius: "50%", border: "3px solid rgba(255,255,255,0.35)", borderTopColor: "#FFFFFF", animation: "rd-spin 1s linear infinite" }} />
              </div>
              <p style={{ ...FREDOKA, color: "#0B1A33", fontSize: 16, fontWeight: 700, margin: 0 }}>Redeeming your gift…</p>
              <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 13, margin: 0 }}>Hang tight, this only takes a moment.</p>
            </div>
          )}

          {status === "success" && (
            <div className="space-y-4">
              <div
                className="mx-auto flex items-center justify-center"
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: 20,
                  background: "linear-gradient(135deg, #0065F8 0%, #00CAFF 100%)",
                  color: "#FFFFFF",
                  boxShadow: "0px 5px 0px #073B8E, 0px 14px 28px rgba(0,101,248,0.30)",
                }}
              >
                <svg width="34" height="34" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span style={{ ...FREDOKA, color: "#0065F8", fontSize: 11, fontWeight: 800, letterSpacing: "0.20em", textTransform: "uppercase" }}>
                Success
              </span>
              <h1
                style={{
                  ...FREDOKA,
                  color: "#0B1A33",
                  fontWeight: 700,
                  fontSize: "clamp(1.6rem, 3.4vw, 2.2rem)",
                  textTransform: "uppercase",
                  letterSpacing: "-0.02em",
                  lineHeight: 1.05,
                  margin: "6px 0 6px",
                }}
              >
                Gift <span style={{ color: "#0065F8" }}>redeemed!</span>
              </h1>
              <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, margin: 0 }}>
                Your premium access is now active.
              </p>
              <div
                className="inline-flex items-center gap-2 mx-auto"
                style={{
                  background: "linear-gradient(180deg, #EAF3FF 0%, #DBEAFE 100%)",
                  border: "2px solid rgba(0,101,248,0.22)",
                  borderRadius: 14,
                  padding: "10px 16px",
                  boxShadow: "0px 2px 0px rgba(11,26,51,0.04)",
                }}
              >
                <svg width="16" height="16" fill="none" stroke="#0065F8" strokeWidth="2.4" viewBox="0 0 24 24">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16 2v4M8 2v4M3 10h18" />
                </svg>
                <span style={{ ...FREDOKA, color: "#0B1A33", fontSize: 13, fontWeight: 700 }}>
                  Premium until <span style={{ color: "#0065F8" }}>{premiumUntil || "your benefits are live"}</span>
                </span>
              </div>
              <div className="pt-2">
                <button
                  type="button"
                  onClick={() => navigate("/storyverse")}
                  className="inline-flex items-center gap-2 transition active:scale-[0.97]"
                  style={{
                    ...FREDOKA,
                    background: "#0065F8",
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: 15,
                    border: "none",
                    borderRadius: 999,
                    padding: "13px 24px",
                    boxShadow: "0px 4px 0px #073B8E, 0px 12px 24px rgba(0,101,248,0.22)",
                    cursor: "pointer",
                  }}
                >
                  Start creating
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {status === "error" && (
            <div className="space-y-4">
              <div
                className="mx-auto flex items-center justify-center"
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: 20,
                  background: "rgba(220,38,38,0.10)",
                  color: "#DC2626",
                  border: "2px solid rgba(220,38,38,0.22)",
                }}
              >
                <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              </div>
              <span style={{ ...FREDOKA, color: "#B91C1C", fontSize: 11, fontWeight: 800, letterSpacing: "0.20em", textTransform: "uppercase" }}>
                Couldn't redeem
              </span>
              <h1
                style={{
                  ...FREDOKA,
                  color: "#B91C1C",
                  fontWeight: 700,
                  fontSize: "clamp(1.4rem, 3vw, 1.9rem)",
                  textTransform: "uppercase",
                  letterSpacing: "-0.01em",
                  lineHeight: 1.05,
                  margin: "4px 0 4px",
                }}
              >
                Unable to redeem
              </h1>
              <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, margin: 0 }}>
                {errorMessage}
              </p>
              <div className="pt-2">
                <button
                  type="button"
                  onClick={() => navigate("/storyverse")}
                  className="inline-flex items-center gap-2 transition active:scale-[0.97]"
                  style={{
                    ...FREDOKA,
                    background: "#FFFFFF",
                    color: "#0065F8",
                    fontWeight: 700,
                    fontSize: 14,
                    border: "2px solid rgba(0,101,248,0.22)",
                    borderRadius: 999,
                    padding: "11px 20px",
                    boxShadow: "0px 2px 0px rgba(11,26,51,0.05)",
                    cursor: "pointer",
                  }}
                >
                  Continue to Storyverse
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`@keyframes rd-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
